import { Injectable, Output, EventEmitter } from "@angular/core";
import { environment } from "src/environments/environment";
import { HttpClient } from "@angular/common/http";
import { FormLogType, IAssetStatus, IForms, IGridLogFetchOption, ISaveAnswer, ISubmitForm } from "src/app/models";
import { RoutingService } from "src/app/services";
import { lastValueFrom } from "rxjs";
import { Helpers } from "../helpers";

@Injectable({
  providedIn: 'root'
})
export class PatientFormsService {
  private lookups = new Map<string, any>();
  private PatientForms_URL = environment.triportFormsService.url + environment.triportFormsService.apiVersion;
  private patientForms = "Forms";

  constructor(private http: HttpClient) { }

  /**
   * 
   * forms for grid
   */
  getFormsList(sponsorName: string, portalId: number, feature: string = "Forms") {
    return new Promise<Array<IForms>>((resolve, reject) => {
      lastValueFrom(this.http.get<any>(this.PatientForms_URL + feature + `/${sponsorName}/${portalId}`))
        .then((forms: Array<IForms>) => {
          resolve(forms);
        }, (err) => {
          reject(err);
        });
    });
  }

  /**
   * 
   * forms for dropdown
   */
  getAllFormsList(sponsorName: string, portalId: number, feature: string = "Forms") {
    return new Promise<Array<IForms>>((resolve, reject) => {
      if (this.lookups.has(this.patientForms + '_' + feature + '_' + portalId)) {
        resolve(this.lookups.get(this.patientForms + '_' + feature + '_' + portalId));
      } else {
        lastValueFrom(this.http.get<any>(this.PatientForms_URL + feature + '/All/' + sponsorName + '/' + portalId))
          .then((forms: Array<IForms>) => {
            this.lookups.set(this.patientForms + '_' + feature + '_' + portalId, forms);
            resolve(forms);
          }, (err) => {
            reject(err);
          });
      }
    });
  }

  /**
  * 
  * Surveys for grid
  */
  getSurveysList(sponsorName: string, portalId: number) {
    return this.getFormsList(sponsorName, portalId, 'Surveys');
  }

  /**
   * 
   * Surveys for dropdown
   */
  getAllSurveysList(sponsorName: string, portalId: number) {
    return this.getAllFormsList(sponsorName, portalId, 'Surveys');
  }

  /**
   * 
   * forms Logs list
   */
  getLogsList(sponsorName: string, portalId: number, option: IGridLogFetchOption, feature: string = "Forms") {
    return new Promise<any>((resolve, reject) => {
      lastValueFrom(this.http.post<any>(this.PatientForms_URL + feature + '/GetLogs/' + sponsorName + '/' + portalId, option))
        .then((logs: any) => {
          resolve(logs);
        }, (err) => {
          reject(err);
        });
    });
  }

  /**
  * 
  * Surveys Logs list for grid
  */
  getSurveysLogsList(sponsorName: string, portalId: number, option: IGridLogFetchOption) {
    return this.getLogsList(sponsorName, portalId, option, 'Surveys');
  }

  exportLogs(sponsorName: string, portalId: number, option: IGridLogFetchOption, feature: string = "Forms") {
    return new Promise<any>((resolve, reject) => {
      lastValueFrom(this.http.post<any>(this.PatientForms_URL + feature + '/ExportLog/' + sponsorName + '/' + portalId, option))
        .then((logs: any) => {
          resolve(logs);
        }, (err) => {
          reject(err);
        });
    });
  }

  exportSurveysLogs(sponsorName: string, portalId: number, option: IGridLogFetchOption) {
    return this.exportLogs(sponsorName, portalId, option, 'Surveys');
  }

  getFormStatus(sponsorName: string, portalId: number, formId: number) {
    return new Promise<Array<any>>((resolve, reject) => {
      lastValueFrom(this.http.get<any>(this.PatientForms_URL + 'Forms/FormStatus/' + sponsorName + '/' + portalId+ '/' + formId))
        .then((formstatus: Array<any>) => {
          resolve(formstatus);
        }, (err) => {
          reject(err);
        });
    });
  }

  getCountry(sponsorName: string, portalId: number, formId: number) {
    return new Promise<Array<any>>((resolve, reject) => {
      lastValueFrom(this.http.get<any>(this.PatientForms_URL + 'Forms/Country/' + sponsorName + '/' + portalId + '/' + formId))
        .then((country: Array<any>) => {
          resolve(country);
        }, (err) => {
          reject(err);
        });
    });
  }
  getInvestigatorName(sponsorName: string, portalId: number, formId: number,SiteId:number,LogFeatureType:FormLogType,StatusId:number) {
    return new Promise<Array<any>>((resolve, reject) => {
      lastValueFrom(this.http.get<any>(this.PatientForms_URL + 'Forms/Investigator/' + sponsorName + '/' + portalId + '/' + formId+ '/' + SiteId + '/' + LogFeatureType + '/' + StatusId))
        .then((InvestigatorName: Array<any>) => {
          resolve(InvestigatorName);
        }, (err) => {
          reject(err);
        });
    });
  }
  getRoles(sponsorName: string, portalId: number, formId: number) {
    return new Promise<Array<any>>((resolve, reject) => {
      lastValueFrom(this.http.get<any>(this.PatientForms_URL + 'Forms/Roles/' + sponsorName + '/' + portalId + '/' + formId))
        .then((roles: Array<any>) => {
          resolve(roles);
        }, (err) => {
          reject(err);
        });
    });
  }

  getSites(sponsorName: string, portalId: number, formId: number) {
    return new Promise<Array<any>>((resolve, reject) => {
      lastValueFrom(this.http.get<any>(this.PatientForms_URL + 'Forms/Sites/' + sponsorName + '/' + portalId + '/' + formId))
        .then((sites: Array<any>) => {
          resolve(sites);
        }, (err) => {
          reject(err);
        });
    });
  }

  getFormDetails(sponsorName: string, portalId: number, mid: any, tid: any, uid: any) {
    return new Promise<any>((resolve, reject) => {
      lastValueFrom(this.http.get<any>(this.PatientForms_URL + 'Forms/' + sponsorName + '/' + portalId + '/' + mid + '/' + uid + '/' + tid))
        .then((formDetail: any) => {
          resolve(formDetail);
        }, (err) => {
          reject(err);
        });
    });
  }

  getSurveyDetails(sponsorName: string, portalId: number, mid: any, uid: any) {
    return new Promise<any>((resolve, reject) => {
      let url = this.PatientForms_URL + 'Surveys/' + sponsorName + '/' + portalId + '/' + mid + (Helpers.isNullOrUndefined(uid) ? '' : '/' + uid)
      lastValueFrom(this.http.get<any>(url))
        .then((formDetail: any) => {
          resolve(formDetail);
        }, (err) => {
          reject(err);
        });
    });
  }
  getFormPreviewDetails(sponsorName: string, portalId: number, assetid: any, feature: string = "Forms") {
    return new Promise<any>((resolve, reject) => {
      let url = this.PatientForms_URL + feature + '/Preview/' + sponsorName + '/' + portalId + '/' + assetid
      lastValueFrom(this.http.get<any>(url))
        .then((formDetail: any) => {
          resolve(formDetail);
        }, (err) => {
          reject(err);
        });
    });
  }

  getSurveyPreviewDetails(sponsorName: string, portalId: number, assetid: any) {
    return this.getFormPreviewDetails(sponsorName, portalId, assetid, 'Surveys');
  }

  submitSurvey(sponsorName: string, portalId: number, mid: any) {
    return new Promise<any>((resolve, reject) => {
      lastValueFrom(this.http.put<any>(this.PatientForms_URL + 'Surveys/' + sponsorName + '/' + portalId + '/' + mid, {}))
        .then((logs: any) => {
          resolve(logs);
        }, (err) => {
          reject(err);
        });
    });
  }

  createNewForm(sponsorName: string, portalId: number, formId: any) {
    return new Promise<any>((resolve, reject) => {
      lastValueFrom(this.http.put<any>(this.PatientForms_URL + 'Forms/Create/' + sponsorName + '/' + portalId + '/' + formId, {}))
        .then((newFormDetail: any) => {
          resolve(newFormDetail);
        }, (err) => {
          reject(err);
        });
    });
  }

  saveAnswer(sponsorName: string, portalId: number, ans: ISaveAnswer) {
    return new Promise<any>((resolve, reject) => {
      lastValueFrom(this.http.put<any>(this.PatientForms_URL + 'Forms/SaveAnswer/' + sponsorName + '/' + portalId, ans))
        .then((logs: any) => {
          resolve(logs);
        }, (err) => {
          reject(err);
        });
    });
  }

  submitForm(sponsorName: string, portalId: number, mid:any, tId:any, ans: ISubmitForm) {
    return new Promise<any>((resolve, reject) => {
      lastValueFrom(this.http.put<any>(this.PatientForms_URL + 'Forms/' + sponsorName + '/' + portalId + '/' + mid + '/' + tId, ans))
        .then((logs: any) => {
          resolve(logs);
        }, (err) => {
          reject(err);
        });
    });
  }

  editForm(sponsorName: string, portalId: number, mid: any, tId: any) {
    return new Promise<any>((resolve, reject) => {
      lastValueFrom(this.http.post<any>(this.PatientForms_URL + 'Forms/' + sponsorName + '/' + portalId + '/' + mid + '/' + tId, {}))
        .then((logs: any) => {
          resolve(logs);
        }, (err) => {
          reject(err);
        });
    });
  }

  deleteForm(sponsorName: string, portalId: number, mid: any, tId: any) {
    return new Promise<any>((resolve, reject) => {
      lastValueFrom(this.http.delete<any>(this.PatientForms_URL + 'Forms/' + sponsorName + '/' + portalId + '/' + mid + '/' + tId))
        .then((logs: any) => {
          resolve(logs);
        }, (err) => {
          reject(err);
        });
    });
  }

  getStatus(sponsorName: string, portalId: number, PersonAssetGUID: any): Promise<IAssetStatus> {
    return new Promise<IAssetStatus>((resolve, reject) => {
      this.http.get<IAssetStatus>(this.PatientForms_URL + `Surveys/Status/${sponsorName}/${portalId}/${PersonAssetGUID}`)
        .toPromise()
        .then((documentResponse?: IAssetStatus) => {
          resolve(documentResponse!);
        }, (err) => {
          reject(err);
        });
    });
  }

  postEpaSignature(sponsorName: string, portalId: number, PersonAssetGuid: any) {
    return new Promise<boolean>((resolve, reject) => {
      lastValueFrom(this.http.post<any>(this.PatientForms_URL + `Surveys/PostEpaSignature/${sponsorName}/${portalId}`, JSON.stringify(PersonAssetGuid)))
        .then(
          (response?: any) => {
            resolve(response);
          },
          (err) => {
            reject(err);
          }
        );
    });
  }
  getFormEntryNumber(sponsorName: string, portalId: number, formId: number,SiteId:number,LogFeatureType:FormLogType,StatusId:number) {
    return new Promise<Array<any>>((resolve, reject) => {
      lastValueFrom(this.http.get<any>(this.PatientForms_URL + 'Forms/FormEntryNumber/' + sponsorName + '/' + portalId + '/' + formId + '/' + SiteId + '/' + LogFeatureType + '/' + StatusId))
        .then((FormEntryNumber:any) => {
          resolve(FormEntryNumber);
        }, (err) => {
          reject(err);
        });
    });
  }
  //[Route("FormSubmittedBy/{sponsorName}/{portalId}/{formId}/{SiteId}/{LogFeatureType}/{StatusId}")]
  getFormSubmittedBy(sponsorName: string, portalId: number, formId: number,SiteId:number,LogFeatureType:FormLogType,StatusId:number) {
    return new Promise<Array<any>>((resolve, reject) => {
      lastValueFrom(this.http.get<any>(this.PatientForms_URL + 'Forms/FormSubmittedBy/' + sponsorName + '/' + portalId + '/' + formId + '/' + SiteId + '/' + LogFeatureType + '/' + StatusId))
        .then((FormEntryNumber:any) => {
          resolve(FormEntryNumber);
        }, (err) => {
          reject(err);
        });
    });
  }
}
